import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  admin_cse,
  provider_behavioral,
  provider_case_manager,
  provider_peer,
} from "@/config/roles";
import { Button2 } from "@/components/UI2/atoms/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import ChevronIcon from "@/assets/images/icons2/icon--chevron.svg?component";
import { getRole, isAuthorized, isUserPCP } from "@/redux/actions/auth";
import { isAdminProfile, isProviderProfile } from "@/core/types";
import { Menu2 } from "@/components/UI2/molecules/Menu";
import type { PatientStatus, UUID } from "@/core/types";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { showToast } from "@/providers/ToastProvider";

import { useRemoveFromWaitList } from "../../api/mutations/useRemoveFromWaitList";
import { useUpdatePatientStatus } from "../../api/mutations/useUpdatePatientStatus";

import { PatientStatusChangeModal } from "./PatientStatusChangeModal";

type PatientStatusMenuProps = {
  is_wait_listed: boolean | undefined;
  isInactive: boolean;
  patientId: UUID;
  patientName: string;
  previous_status: PatientStatus | undefined;
};

export function PatientStatusMenu({
  is_wait_listed,
  isInactive,
  patientId,
  patientName,
  previous_status,
}: PatientStatusMenuProps) {
  const history = useHistory();
  const profile = useSelector(selectProfile);
  const [showChangeStatusPopUp, setShowChangeStatusPopUp] = useState(false);

  const newPatientStatus = isInactive ? (previous_status ?? "ACTIVE 90-DAY") : "INACTIVE";

  const { mutateAsync: updateStatus } = useUpdatePatientStatus({
    onSuccess: () => setShowChangeStatusPopUp(false),
  });

  const { mutateAsync: removeFromWaitList } = useRemoveFromWaitList({
    onSuccess: () =>
      showToast([{ heading: `${patientName} is no longer waitlisted.`, variant: "success" }]),
  });

  function showWaitListOption() {
    const role = getRole();
    const isIntake =
      role === provider_behavioral &&
      isProviderProfile(profile) &&
      profile.provider_type === "BEHAVIORAL_INTAKE";
    const isCSE = role === admin_cse && isAdminProfile(profile) && profile.user_type === "CSE";

    return isIntake || isCSE;
  }

  const menuOptions = [
    {
      action: () => setShowChangeStatusPopUp(true),
      shouldShow: () =>
        !isUserPCP() && !isAuthorized(provider_peer) && !isAuthorized(provider_case_manager),
      title: isInactive ? "Reactivate" : "Inactivate",
    },
    {
      action: async () => {
        try {
          await removeFromWaitList({ patientId });
        } catch (error) {
          captureErrorMessage(parseError({ error }), { tags: { patientId } });
          showToast([
            { heading: "Failed to remove from waitlist. Please try again.", variant: "error" },
          ]);
        }
      },
      shouldShow: () => showWaitListOption() && !!is_wait_listed,
      title: "Remove from waitlist",
    },
    {
      action: () => history.push(`/app/patient/${patientId}/waitlist_form`),
      shouldShow: () => showWaitListOption() && !is_wait_listed,
      title: "Waitlist",
    },
  ];

  const visibleOptions = menuOptions.filter((o) => o.shouldShow());

  if (visibleOptions.length === 0) return null;

  return (
    <>
      {showChangeStatusPopUp && (
        <PatientStatusChangeModal
          cancelHandler={() => setShowChangeStatusPopUp(false)}
          confirmHandler={async (reason?: string) => {
            await updateStatus({
              patientId,
              providerName: isProviderProfile(profile) ? profile.name : undefined,
              reason,
              status: newPatientStatus,
            });
          }}
          newPatientStatus={newPatientStatus}
          oldPatientStatus={previous_status}
          patientName={patientName}
        />
      )}

      <Menu2
        items={visibleOptions.map((o) => ({ id: o.title, label: o.title }))}
        onAction={(key) => visibleOptions.find((o) => o.title === key)?.action()}
        placement="bottom left"
        trigger={
          <Button2 aria-label="Open patient menu" rawChildren variant="blank">
            <ChevronIcon className="tw:text-fill-teal-primary" />
          </Button2>
        }
      />
    </>
  );
}
